import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/shared/lib/utils';
import { Typography } from '../Typography/Typography';
import Arrow from '@/assets/icons/selectOpenClose.svg?react';

interface SelectOption {
  name: string;
  value: string | number;
}

interface SelectProps extends React.HTMLAttributes<HTMLDivElement> {
  list: SelectOption[];
  name: string;
  current: string | number | null;
  handleChange?: (name: string, value: string | number) => void;
  placeholder?: React.ReactNode;
  up?: boolean;
  wrClassName?: string;
  className?: string;
  isBlack?: string;
}

export const Select: React.FC<SelectProps> = ({
  list,
  name,
  current,
  handleChange,
  placeholder,
  up = false,
  wrClassName,
  className,
  isBlack,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (value: string | number) => {
    handleChange?.(name, value);
    setIsOpen(false);
  };

  const selectedOption = list.find((item) => item.value === current);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={cn('relative flex w-full flex-col gap-2', wrClassName)}
      ref={containerRef}
      {...props}
    >
      <div
        className={cn(
          'flex h-[54px] w-full cursor-pointer items-center justify-between gap-5 rounded-full bg-[white] px-[15px]',
          isBlack && 'bg-black',
          className
        )}
        onClick={toggleDropdown}
      >
        <Typography
          className={cn(
            'truncate whitespace-nowrap text-[16px] font-[400]',
            isBlack && 'text-[white]'
          )}
        >
          {selectedOption ? selectedOption.name : placeholder || 'Выберите...'}
        </Typography>
        <Arrow
          className={cn(
            'flex-shrink-0 transition-transform',
            isOpen && 'rotate-180',
            isBlack && 'stroke-white'
          )}
        />
      </div>

      {isOpen && (
        <ul
          className={cn(
            'absolute top-[calc(100%+2px)] z-20 flex w-full min-w-max max-w-[300px] flex-col gap-[8px] overflow-hidden rounded-2xl bg-white py-[12px] shadow-lg',
            isBlack && 'bg-black'
          )}
        >
          {list.map((item) => (
            <li
              key={item.value}
              className={cn(
                'cursor-pointer break-words px-[16px] py-[4px]',
                item.value === current && 'bg-[#0000004D]',
                isBlack && item.value === current && 'bg-[#FFFFFF99]',
                isBlack ? 'hover:bg-[#FFFFFF99]' : 'hover:bg-[#0000004D]'
              )}
              onClick={() => handleSelect(item.value)}
            >
              <Typography
                className={cn(
                  'text-[16px] font-[400]',
                  isBlack && 'text-white'
                )}
              >
                {item.name}
              </Typography>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
